/**
 * One phase's Success Measure rule (FR-PLAY-07).
 *
 * Two shapes of rule exist, and the card renders whichever applies:
 *   - **Yardage rules** (offense run/pass) get editable early- and late-down
 *     thresholds, each of which may be a yard count or "whatever gains a first
 *     down" (the chains).
 *   - **Fixed rules** (defense, special teams) are stated in prose, because
 *     "a make is a success" has nothing to configure.
 */
import { DISTANCE_TO_FIRST_DOWN } from '../../lib/api-types';
import type { SuccessMeasurePhaseRule, YardageThreshold } from '../../lib/api-types';

/** Widest threshold a Team Admin can set: beyond this it is not a football rule. */
export const MAX_YARDS = 99;
const CHAINS_LABEL = 'Whatever gains a first down';
/** Threshold used when the admin unchecks "chains" and needs a starting number. */
const DEFAULT_YARDS = 4;
/** Platform default for `late_down_fallback_yards` (plays service). */
export const DEFAULT_LATE_DOWN_FALLBACK_YARDS = 10;

/** Human copy for the fixed rules that are not yardage thresholds. */
export const RULE_COPY: Record<string, string> = {
  inverse_of_offensive_success_measure:
    'A stop is a success: the inverse of the offensive measure.',
  make_is_success: 'A make is a success; a miss is not.',
  inside_20_is_success: 'Pinning the opponent inside the 20 is a success.',
  opponent_miss_is_success: 'Blocking the opponent’s kick: a miss or block is a success.',
  return_yards_threshold: 'A return past the yardage threshold is a success.',
  touchback_or_inside_25_is_success: 'A touchback or a stop inside the 25 is a success.',
};

function thresholdInputValue(threshold: YardageThreshold | undefined): string {
  if (threshold === undefined || threshold === DISTANCE_TO_FIRST_DOWN) return '';
  return String(threshold);
}

interface ThresholdFieldProps {
  id: string;
  label: string;
  value: YardageThreshold | undefined;
  isDisabled: boolean;
  error: string | undefined;
  onChange: (next: YardageThreshold) => void;
}

function ThresholdField({
  id,
  label,
  value,
  isDisabled,
  error,
  onChange,
}: ThresholdFieldProps): JSX.Element {
  const isChains = value === DISTANCE_TO_FIRST_DOWN;
  return (
    <div className={`field field--inline${error === undefined ? '' : ' field--invalid'}`}>
      <label className="field__label" htmlFor={id}>
        {label}
      </label>
      <div className="team-settings__threshold">
        <input
          id={id}
          className="field__control field__control--num"
          type="number"
          min={0}
          max={MAX_YARDS}
          step={1}
          disabled={isDisabled || isChains}
          value={thresholdInputValue(value)}
          onChange={(event) => {
            const parsed = Number.parseInt(event.target.value, 10);
            onChange(Number.isFinite(parsed) ? parsed : 0);
          }}
          aria-invalid={error === undefined ? undefined : true}
        />
        <label className="team-settings__checkbox">
          <input
            type="checkbox"
            checked={isChains}
            disabled={isDisabled}
            onChange={(event) =>
              onChange(event.target.checked ? DISTANCE_TO_FIRST_DOWN : DEFAULT_YARDS)
            }
          />
          {CHAINS_LABEL}
        </label>
      </div>
      {error === undefined ? null : (
        <span className="field__error" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}

export interface PhaseCardProps {
  /** Stable prefix for input ids and error keys, e.g. `offense.run`. */
  path: string;
  label: string;
  rule: SuccessMeasurePhaseRule;
  isDisabled: boolean;
  errors: Record<string, string>;
  onChange: (rule: SuccessMeasurePhaseRule) => void;
}

export function PhaseCard({
  path,
  label,
  rule,
  isDisabled,
  errors,
  onChange,
}: PhaseCardProps): JSX.Element {
  const hasThresholds = rule.early_down_yards !== undefined || rule.late_down_yards !== undefined;
  return (
    <div className="team-settings__phase">
      <div className="team-settings__phase-head">
        <h3>{label}</h3>
        <label className="team-settings__checkbox">
          <input
            type="checkbox"
            checked={rule.is_enabled}
            disabled={isDisabled}
            onChange={(event) => onChange({ ...rule, is_enabled: event.target.checked })}
          />
          Measure enabled
        </label>
      </div>

      {hasThresholds ? (
        <>
          <ThresholdField
            id={`${path}-early`}
            label="1st & 2nd down needs"
            value={rule.early_down_yards}
            isDisabled={isDisabled || !rule.is_enabled}
            error={errors[`${path}.early_down_yards`]}
            onChange={(next) => onChange({ ...rule, early_down_yards: next })}
          />
          <ThresholdField
            id={`${path}-late`}
            label="3rd & 4th down needs"
            value={rule.late_down_yards}
            isDisabled={isDisabled || !rule.is_enabled}
            error={errors[`${path}.late_down_yards`]}
            onChange={(next) => onChange({ ...rule, late_down_yards: next })}
          />
          {rule.late_down_yards === DISTANCE_TO_FIRST_DOWN ? (
            <div
              className={`field field--inline${
                errors[`${path}.late_down_fallback_yards`] === undefined ? '' : ' field--invalid'
              }`}
            >
              <label className="field__label" htmlFor={`${path}-fallback`}>
                3rd &amp; 4th down with no spot logged needs
              </label>
              <div className="team-settings__threshold">
                <input
                  id={`${path}-fallback`}
                  className="field__control field__control--num"
                  type="number"
                  min={0}
                  max={MAX_YARDS}
                  step={1}
                  disabled={isDisabled || !rule.is_enabled}
                  value={String(rule.late_down_fallback_yards ?? DEFAULT_LATE_DOWN_FALLBACK_YARDS)}
                  onChange={(event) => {
                    const parsed = Number.parseInt(event.target.value, 10);
                    onChange({
                      ...rule,
                      late_down_fallback_yards: Number.isFinite(parsed) ? parsed : 0,
                    });
                  }}
                  aria-describedby={`${path}-fallback-hint`}
                />
                <span className="muted" id={`${path}-fallback-hint`}>
                  yards (used when the first-down distance can’t be worked out)
                </span>
              </div>
              {errors[`${path}.late_down_fallback_yards`] === undefined ? null : (
                <span className="field__error" role="alert">
                  {errors[`${path}.late_down_fallback_yards`]}
                </span>
              )}
            </div>
          ) : null}
        </>
      ) : (
        <p className="muted">
          {rule.rule === undefined ? 'Fixed rule.' : (RULE_COPY[rule.rule] ?? rule.rule)}
          {rule.thresholds === undefined
            ? ''
            : ` (${Object.entries(rule.thresholds)
                .map(([key, threshold]) => `${key.replace(/_/g, ' ')}: ${String(threshold)}`)
                .join(', ')})`}
        </p>
      )}
    </div>
  );
}
